import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useState } from "react";

const FIELD_CLASS =
  "flex h-12 w-full items-center justify-between rounded-2xl border-0 bg-primary-soft px-4 py-3.5 text-sm font-semibold text-foreground hover:bg-primary-soft";

// Render " → " separators as a lucide ArrowRight icon
type JettyOption = string | {
  mdTerminalId: string;
  mdTerminalName: string;
};

const renderOption = (option: JettyOption) => {
  const label =
    typeof option === "string" ? option : option.mdTerminalName;

  const separator = label.includes(" → ") ? " → " : "-";
  const parts = label.split(separator);
  if (parts.length === 1) return label;

  return (
    <span className="flex min-w-0 items-center gap-1">
      {parts.map((part, index) => {
        const isLast = index === parts.length - 1;
        return (
          <span
            key={index}
            className={cn(
              "flex items-center gap-1",
              isLast ? "min-w-0" : "shrink-0",
            )}
          >
            {index > 0 ? (
              <ArrowRight size={14} className="shrink-0" aria-hidden="true" />
            ) : null}
            <span className={isLast ? "truncate" : undefined}>
              {part.trim()}
            </span>
          </span>
        );
      })}
    </span>
  );
};

type JettySelectProps<T extends JettyOption> = {
  label: string;
  value: T | null;
  options: T[];
  onChange: (value: T) => void;
};

export function JettySelect<T extends JettyOption>({
  label,
  value,
  options,
  onChange,
}: JettySelectProps<T>) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <Label className="text-xs font-bold text-field">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button type="button" className={FIELD_CLASS}>
              <span
                className={cn(
                  "min-w-0 flex-1 truncate text-left text-sm font-medium",
                  value ? "text-muted-foreground" : "text-border",
                )}
              >
                {value ? renderOption(value) : "Select jetty"}
              </span>
              <ChevronDown
                size={16}
                className={cn(
                  "transition-transform duration-200 ease-in-out",
                  open && "rotate-180",
                )}
                aria-hidden="true"
              />
            </Button>
          }
        />
        <PopoverContent
          align="start"
          className="w-(--anchor-width) rounded-2xl shadow-xl"
        >
          {options.map((option) => (
            <button
              key={typeof option === "string" ? option : option.mdTerminalId}
              type="button"
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              className={cn(
                "flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-primary-soft",
                value === option ||
                  (typeof value !== "string" &&
                    typeof option !== "string" &&
                    value?.mdTerminalId === option.mdTerminalId)
                  ? "bg-primary-soft"
                  : undefined,
              )}
            >
              {renderOption(option)}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
